import React, { useState, useEffect, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Tag, notification } from 'antd'
import { request } from '../../api'
import { getCurrentUser } from '../services/user.services'
import { getStripeAccountStatus } from '../services/stripe.services'
import { getMyPartnerEvents } from '../services/partner.services'
import {
    login,
    loginResendOtp,
    loginVerifyEmail,
    loginVerifyOtp,
    loginWithToken,
    verifySignUpEmail,
} from '../services/auth.services'
import { Constants, isValidEmail } from '@/utils'
import { AUTH_PATHS } from '@/components/Auth/constants'
import { validTimezone } from '@/utils/DateHelpers'

const STRIPE_ACCOUNT_STATUS_COLOR = {
    Functional: 'green',
    Blocked: 'red',
    'Blocked soon': 'orange',
}

const AuthContext = React.createContext({
    isLoadingStates: true,
    isLoggedIn: false,
    token: null,
    isAdmin: false,
    isOrga: false,
    isAdminOrOrga: false,
    isPartner: false,
    partnerEvents: [],
    user: {
        id: '',
        type: '',
        email: '',
        firstName: '',
        lastName: '',
        username: '',
        birthday: '',
        club: '',
        gender: '',
        phoneNumber: Constants.DEFAULT_PHONE,
        organisation: '',
        organisation_settings: {
            stripe: {
                account_url: '',
                account_id: '',
                account_completed: '',
            },
            invoice_address: {
                street_number: '',
                city: '',
                country: '',
                zip_code: '',
            },
            VAT_number: '',
            taxe_rate: '',
            phone_number: Constants.DEFAULT_PHONE,
            currency: '',
        },
        timezone: '',
        background: '',
        logo: '',
        systemPreferences: {},
    },
    UpateUserPhoneNumber: (newPhoneNumber) => {},
    userSelect: {
        token: '',
        id: '',
    },
    onLogout: () => {},
    onLogin: async (email, password, setIsLoading) => {},
    onVerifyEmailInLogin: async (email, code, setIsLoading) => {},
    onVerifyOtpInLogin: async (email, code, otpSessionId, setIsLoading) => {},
    onResendOtpInLogin: async (email, otpSessionId) => {},
    onLoginWithToken: async (token, setIsLoading) => {},
    onSignup: async ({ email, password, firstName, lastName, birthday, gender, tshirtSize, club, phoneNumber }) => {},
    getPostAuthRedirect: (fallback = '/') => fallback,
    sendRequest: (method, path, body, callback) => {},
    onSelectUser: (id) => {},
    updateUser: (user, isLogin = false) => {},
    cookiesConsentOpen: false,
    setCookiesConsentOpen: () => {},
    isCompetitionOwner: (orgaId = '') => true || false,
    isJudge: (judges = [{ id: '' }]) => true || false,
    isCheckInAttendant: (checkInAttendants = [{ id: '' }]) => true || false,
    isMediaAccount: (mediaAccounts = [{ id: '' }]) => true || false,
    stripeAccountStatus: {} || undefined,
    refreshStripeAccountStatus: () => {},
    isValidAccount: () => true,
})

const DEFAULT_USER = {
    birthday: '',
    club: '',
    email: '',
    firstName: '',
    gender: '',
    id: '',
    lastName: '',
    phoneNumber: Constants.DEFAULT_PHONE,
    type: '',
    timezone: '',
}

export const AuthContextProvider = (props) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoadingStates, setIsLoadingStates] = useState(true)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [token, setToken] = useState()
    const [user, setUser] = useState(DEFAULT_USER)
    const isAdmin = user?.type === 'admin'
    const isOrga = user?.type === 'orga'
    const isAdminOrOrga = user?.type === 'admin' || user?.type === 'orga'
    const [userSelect, setUserSelect] = useState({
        token: '',
        id: '',
    })
    const [partnerEvents, setPartnerEvents] = useState([])

    const [cookiesConsentOpen, setCookiesConsentOpen] = useState(false)
    const [isPartner, setIsPartner] = useState(false)

    const [stripeAccountStatus, setStripeAccountStatus] = useState()
    const [refreshStripeAccountStatus, setRefreshStripeAccountStatus] = useState(false)

    const fetchCurrentUser = async (token) => {
        let isReloadPending = false
        try {
            const response = await getCurrentUser()

            const userResult = response.data.data

            setToken(token)
            setIsLoggedIn(true)

            updateUser(userResult, true)
            await refreshPartnerStatus()

            // TODO: replace with userSelect when token for admin will be in place
            const orgaId = userResult.orgaId
            if (orgaId) {
                setUserSelect({
                    token: '',
                    id: orgaId,
                })
            }
        } catch (error) {
            console.error('/fetchCurrentUser - error', error?.response)
            if (error?.response?.status === 401) {
                isReloadPending = true
                ClearAll()
                setTimeout(() => window.location.reload(), 500)
            }
        } finally {
            // Keep the loader up until the reload happens: rendering as logged-out even
            // briefly makes PrivateRoute replace the history entry with /connect and
            // mounted pages fire token-less requests.
            if (!isReloadPending) setIsLoadingStates(false)
        }
    }

    useEffect(() => {
        const storedToken = localStorage.getItem('token')
        if (storedToken) {
            fetchCurrentUser(storedToken)
        } else {
            ClearAll()
            setIsLoadingStates(false)
        }
    }, [])

    useEffect(() => {
        if (user?.email && window.Bugpilot?.identify) {
            window.Bugpilot.identify(user.email, { email: user.email })
        }
    }, [user])

    useEffect(() => {
        if (!isLoggedIn) {
            setIsPartner(false)
            setPartnerEvents([])
            return
        }

        refreshPartnerStatus()
    }, [isLoggedIn])

    useEffect(() => {
        let timeout
        const checkStripeStatus = async () => {
            if (stripeAccountStatus && stripeAccountStatus.checkedAt) {
                const toSoonToRefresh = Date.now() - new Date(stripeAccountStatus.checkedAt) < 60000

                if (toSoonToRefresh) {
                    return
                }
            }

            try {
                const response = await getStripeAccountStatus(user.organisation_settings.stripe.account_id)

                const { status, reason } = response.data

                setStripeAccountStatus({
                    ...response.data,
                    checkedAt: new Date().toISOString(),
                    color: STRIPE_ACCOUNT_STATUS_COLOR[status],
                    label: status,
                })

                if (status !== 'Functional') {
                    notification.error({
                        message: 'Your stripe account requires your attention !',
                        description: (
                            <div>
                                <h3>
                                    Stripe account status :{' '}
                                    <Tag color={STRIPE_ACCOUNT_STATUS_COLOR[status]}>{status}</Tag>
                                </h3>
                                <p>{reason}</p>
                                <br />
                                <p>
                                    You can solve the issue by connecting into your{' '}
                                    <a href="https://dashboard.stripe.com/">Stripe Dashboard</a>
                                </p>
                            </div>
                        ),
                        key: 'stripe-account-status',
                        duration: 0,
                    })
                }
            } catch (error) {
                console.error('/checkStripeStatus - error', error)
            }
        }
        if (user?.organisation_settings?.stripe?.account_id && user?.organisation_settings?.stripe?.account_completed) {
            checkStripeStatus()

            timeout = setTimeout(() => {
                setRefreshStripeAccountStatus((previous) => !previous)
            }, 60 * 60 * 1000) // every one hour
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [user?.organisation_settings?.stripe?.account_id, refreshStripeAccountStatus])

    const refreshPartnerStatus = async () => {
        if (!isLoggedIn) {
            setIsPartner(false)
            setPartnerEvents([])
            return false
        }

        try {
            const { data } = await getMyPartnerEvents()

            console.log('refreshPartnerStatus - data', data)
            const nextIsPartner = !!data?.isPartner
            setIsPartner(nextIsPartner)
            setPartnerEvents(data?.events || [])
            return nextIsPartner
        } catch (error) {
            console.error('/refreshPartnerStatus - error', error)
            setIsPartner(false)
            setPartnerEvents([])
            return false
        }
    }

    const ClearAll = () => {
        localStorage.clear()
        sessionStorage.clear()
        setIsLoggedIn(false)
        setToken()
        setUser(DEFAULT_USER)
        setUserSelect({ token: '', id: '' })
        setIsPartner(false)
        setPartnerEvents([])
    }

    const UpateUserPhoneNumber = (newPhoneNumber) => {
        localStorage.setItem('phoneNumber', JSON.stringify(newPhoneNumber))
        localStorage.setItem('user', JSON.stringify({ ...user, phoneNumber: newPhoneNumber }))
        setUser((previous) => (previous = { ...previous, phoneNumber: newPhoneNumber }))
    }

    const onLogout = async (to) => {
        ClearAll()

        if (to) {
            navigate(to)
        }
    }

    const onSignup = async (data, setIsLoading) => {
        try {
            setIsLoading(true)
            ClearAll()

            const response = await verifySignUpEmail(data)

            const signupData = response.data?.data
            if (!signupData?.token) {
                throw new Error('Invalid signup response')
            }

            localStorage.setItem('token', signupData.token)
            setToken(signupData.token)
            updateUser(signupData.user, true)

            setIsLoggedIn(true)
            notification.success({
                message: 'New account created successfully!',
            })

            // Same as login: keep loader until navigation completes
            setTimeout(() => {
                window.location.href = getPostAuthRedirect('/my-account')
            }, 500)
        } catch (error) {
            setIsLoading(false)
            return { error: error.response?.data?.message || error.message || 'Oops, an error occurred !' }
        }
    }

    const updateUserLocalStorage = (user) => {
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem(user.type === 'athlete' ? 'athleteId' : 'orgaId', user.id)
    }

    const updateUser = (newUser, isLogin = false) => {
        const tmpUser = isLogin ? newUser : { ...user, ...newUser }

        tmpUser.username = `${tmpUser.firstName} ${tmpUser.lastName}`
        // a stored zone this browser rejects would throw in every dayjs.tz(…) (white screen, Oct 31 2026)
        tmpUser.timezone = validTimezone(tmpUser.timezone)

        setUser(tmpUser)
        updateUserLocalStorage(tmpUser)
    }

    // Where to land after a successful login. Priority: the page a PrivateRoute
    // bounced the user from > the page under the auth modal (backgroundLocation)
    // > the current page (token logins on arbitrary pages) > fallback.
    // fallback is used when we have no idea where the user came from (direct link to
    // /connect or /sign-up). Signup passes '/my-account', login stays on '/'.
    const getPostAuthRedirect = (fallback = '/') => {
        // Ignore a `from` pointing back at an auth page: switching between /connect and
        // /sign-up carries the state along, and we must not redirect into the form again.
        const from = location.state?.from
        if (from?.pathname && !AUTH_PATHS.includes(from.pathname)) return from.pathname + (from.search || '')

        const background = location.state?.backgroundLocation
        if (background?.pathname) return background.pathname + (background.search || '')

        return AUTH_PATHS.includes(location.pathname) ? fallback : location.pathname + location.search
    }

    const onLogin = async (email, password, setIsLoading) => {
        try {
            setIsLoading(true)
            ClearAll()
            const response = await login(email, password)

            if (response.data?.data?.MESSAGE_CODE === 'NEED_VERIFY_EMAIL') {
                setIsLoading(false)
                return 'NEED_VERIFY_EMAIL'
            }
            const loginData = response.data?.data

            // two-factor authentication: no token yet, the caller shows the code screen (Auth OtpScreen)
            if (loginData?.MESSAGE_CODE === 'NEED_OTP' || loginData?.requiresOtp) {
                setIsLoading(false)
                return { requiresOtp: true, email: loginData.email || email, otpSessionId: loginData.otpSessionId }
            }
            localStorage.setItem('token', loginData.token)

            // Important : Need timeout to update local storages
            setTimeout(() => {
                window.location.href = getPostAuthRedirect()
            }, 500)
        } catch (error) {
            setIsLoading(false)
            notification.error({
                message: error.response?.data.message,
                description: error.response?.data.error,
                duration: 10,
            })
            return { error: true }
        }
    }

    const onVerifyEmailInLogin = async (email, code, setIsLoading) => {
        try {
            setIsLoading(true)
            ClearAll()
            const response = await loginVerifyEmail(email, code)

            const loginData = response.data?.data
            localStorage.setItem('token', loginData.token)

            // Important : Need timeout to update local storages
            setTimeout(() => {
                window.location.href = getPostAuthRedirect()
            }, 500)
        } catch (error) {
            setIsLoading(false)
            notification.error({
                message: error.response?.data.message,
                description: error.response?.data.error,
                duration: 10,
            })
            return { error: true }
        }
    }

    // Two-factor login, step 2: same landing as onVerifyEmailInLogin once the code is accepted.
    const onVerifyOtpInLogin = async (email, code, otpSessionId, setIsLoading) => {
        try {
            setIsLoading(true)
            ClearAll()
            const response = await loginVerifyOtp(email, code, otpSessionId)

            const loginData = response.data?.data
            localStorage.setItem('token', loginData.token)

            // Important : Need timeout to update local storages
            setTimeout(() => {
                window.location.href = getPostAuthRedirect()
            }, 500)
        } catch (error) {
            setIsLoading(false)
            notification.error({
                message: error.response?.data?.message || 'Oops, an error occurred !',
                description: error.response?.data?.error,
                duration: 10,
            })
            return { error: true, status: error.response?.status }
        }
    }

    // Resolves true when a new code went out; the OtpScreen restarts its cooldown on that.
    const onResendOtpInLogin = async (email, otpSessionId) => {
        try {
            await loginResendOtp(email, otpSessionId)
            return true
        } catch (error) {
            // the login was burnt (too many resends) or the IP is rate limited: back to the password step
            if (error.response?.status === 429) setTimeout(() => window.location.reload(), 1000)

            notification.error({ message: error.response?.data?.message || 'Oops, an error occurred !' })
            return false
        }
    }

    const onLoginWithToken = async (token, setIsLoading) => {
        setIsLoading(true)
        try {
            const response = await loginWithToken(token)

            const loginData = response.data?.data
            // New state user
            setIsLoggedIn(true)
            localStorage.setItem('token', loginData.token)
            setToken(loginData.token)
            updateUser(loginData.user, true)

            // Important : Need timeout to update local storages
            setTimeout(() => {
                window.location.href = getPostAuthRedirect()
            }, 500)
        } catch (error) {
            setIsLoading(false)
            notification.error({
                message: error.response?.data.message,
                description: error.response?.data.error,
                duration: 10,
            })
            return { error: true }
        }
    }

    const sendRequest = async (method, path, body, callback) => {
        const response = await request(method, path, body, token)

        if (!response.error) {
            callback(response.data)
        } else {
            throw response.data?.message
        }
    }

    const onSelectUser = async (id) => {
        sendRequest('post', 'user/auth-as', { userId: id }, (response) => {
            const structuredResponseToStore = {
                token: response.token,
                id: response.id,
            }
            setUserSelect(structuredResponseToStore)
        })
    }

    const isCompetitionOwner = (orgaId) => {
        if (isAdmin) {
            return true
        }

        if (user.id === orgaId) {
            return true
        }

        return false
    }

    const isJudge = (judges) => {
        return judges?.some((o) => o.id === user.id)
    }

    const isCheckInAttendant = (checkInAttendants) => {
        return checkInAttendants?.some((o) => o.id === user.id)
    }

    const isMediaAccount = (mediaAccounts) => {
        return mediaAccounts?.some((o) => o.id === user.id)
    }

    const isValidAccount = () => {
        return (
            user.firstName &&
            user.lastName &&
            user.email &&
            isValidEmail(user.email) &&
            user.phoneNumber &&
            user.phoneNumber.phone?.trim() !== '' &&
            user.birthday &&
            user.gender &&
            user.tshirtSize
        )
    }

    return (
        <AuthContext.Provider
            value={{
                isLoadingStates,
                isLoggedIn,
                token,
                isAdmin,
                isOrga,
                isAdminOrOrga,
                isPartner,
                partnerEvents,
                user,
                UpateUserPhoneNumber,
                userSelect,
                onLogout,
                onLogin,
                onVerifyEmailInLogin,
                onVerifyOtpInLogin,
                onResendOtpInLogin,
                onLoginWithToken,
                onSignup,
                getPostAuthRedirect,
                sendRequest,
                onSelectUser,
                updateUser,
                refreshPartnerStatus,
                cookiesConsentOpen,
                setCookiesConsentOpen,
                isCompetitionOwner,
                isJudge,
                isCheckInAttendant,
                isMediaAccount,
                stripeAccountStatus,
                refreshStripeAccountStatus: () => setRefreshStripeAccountStatus((previous) => !previous),
                isValidAccount,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    )
}

export default AuthContext

export const useAuth = () => useContext(AuthContext)
